import api from '../utils/api';

// In-flight request cache to coalesce concurrent identical GETs
const inFlightCrewRequests = new Map();

export const searchUsers = async (query) => {
  if (!query || typeof query !== 'string') {
    throw new Error('Query must be a non-empty string');
  }

  try {
    const response = await api.get(`/crew/users/search`, {
      params: { query }
    });
    return response.data || []; // Ensure we return an array
  } catch (error) {
    console.error('Error searching users:', error);
    throw error;
  }
};

// Combined helper: update admin flag and permissions in one flow
export const updateMemberAccess = async (
  projectId,
  memberId,
  {
    // Set these from the dialog's edited state
    isAdmin: desiredIsAdmin,
    // Provide the original value so we only call admin endpoint when needed
    initialIsAdmin,
    // Partial permissions delta object, e.g. { 'scripts.manage': true, 'calendar.manage': false }
    permissionsDelta = null,
    // Optional meta passed to admin toggle endpoint
    meta = {}
  } = {}
) => {
  if (!projectId || !memberId) {
    throw new Error('Project ID and Member ID are required');
  }

  try {
    const results = { admin: null, permissions: null };

    // 1) Toggle admin only if changed
    const adminChanged = typeof desiredIsAdmin === 'boolean' && desiredIsAdmin !== initialIsAdmin;
    if (adminChanged) {
      results.admin = await toggleAdminStatus(projectId, memberId, desiredIsAdmin, meta);
    }

    // 2) Patch permissions if provided and non-empty
    if (permissionsDelta && typeof permissionsDelta === 'object' && Object.keys(permissionsDelta).length > 0) {
      results.permissions = await updateMemberPermissions(projectId, memberId, permissionsDelta);
    }

    return results;
  } catch (error) {
    // Surface first error
    throw error;
  }
};

// Permissions registry and role templates
export const getPermissionsRegistry = async () => {
  try {
    const response = await api.get('/crew/permissions/registry');
    return response.data;
  } catch (error) {
    console.error('Error fetching permissions registry:', error);
    throw new Error(error.response?.data?.message || 'Failed to load permissions');
  }
};

export const getRoleTemplates = async () => {
  try {
    const response = await api.get('/crew/roles/templates');
    return response.data;
  } catch (error) {
    console.error('Error fetching role templates:', error);
    throw new Error(error.response?.data?.message || 'Failed to load role templates');
  }
};

export const updateMemberPermissions = async (projectId, memberId, partialPermissions) => {
  if (!projectId || !memberId) {
    throw new Error('Project ID and Member ID are required');
  }
  if (!partialPermissions || typeof partialPermissions !== 'object') {
    throw new Error('partialPermissions object is required');
  }
  try {
    const response = await api.patch(`/crew/projects/${projectId}/members/${memberId}/permissions`, {
      permissions: partialPermissions
    });
    return response.data;
  } catch (error) {
    console.error('Error updating member permissions:', error);
    if (error.response?.status === 403) {
      throw new Error('You do not have permission to modify member permissions.');
    }
    throw new Error(error.response?.data?.message || 'Failed to update permissions');
  }
};

export const getProjectCrew = async (projectId, options = {}) => {
  if (!projectId) {
    throw new Error('Project ID is required');
  }

  try {
    const { force = false } = options;

    // Return existing promise if a request for this projectId is already in flight
    if (!force && inFlightCrewRequests.has(projectId)) {
      return inFlightCrewRequests.get(projectId);
    }

    const cacheBuster = force ? `?_=${Date.now()}` : '';
    const requestPromise = api
      .get(`/crew/projects/${projectId}/crew${cacheBuster}`)
      .then((response) => {
        console.log('Fetched crew members:', response.data);
        return response.data;
      })
      .finally(() => {
        inFlightCrewRequests.delete(projectId);
      });

    inFlightCrewRequests.set(projectId, requestPromise);
    const data = await requestPromise;
    return data;
  } catch (error) {
    console.error('Error getting project crew:', error);
    throw error.response?.data || { message: 'Failed to get project crew' };
  }
};

export const toggleAdminStatus = async (projectId, memberId, isAdmin, meta = {}) => {
  if (!projectId) {
    throw new Error('Project ID is required');
  }

  if (!memberId) {
    throw new Error('Member ID is required');
  }

  try {
    console.log('Toggling admin status:', { projectId, memberId, isAdmin });
    const response = await api.patch(
      `/crew/projects/${projectId}/members/${memberId}/admin`,
      { isAdmin: !!isAdmin, ...meta }
    );
    
    // Verify the response contains the expected data
    if (!response.data || !response.data.crewMember) {
      console.warn('Server response missing crewMember data:', response.data);
      // Return a fabricated response to maintain UI state
      return {
        message: `Admin privileges ${isAdmin ? 'granted' : 'revoked'} successfully`,
        crewMember: { isAdmin: !!isAdmin }
      };
    }
    
    // Verify the isAdmin field in the response matches what we sent
    if (response.data.crewMember.isAdmin !== !!isAdmin) {
      console.warn('Server returned inconsistent isAdmin value:', {
        sent: !!isAdmin,
        received: response.data.crewMember.isAdmin
      });
      // Fix the response to match what we sent
      response.data.crewMember.isAdmin = !!isAdmin;
    }
    
    console.log('Admin status updated:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error updating admin status:', {
      error: error.response?.data || error.message,
      status: error.response?.status,
      projectId,
      memberId
    });
    
    // Handle specific error cases
    if (error.response?.status === 401) {
      throw new Error('Authentication required. Please log in again.');
    } else if (error.response?.status === 403) {
      throw new Error('You do not have permission to modify admin privileges.');
    } else if (error.response?.status === 404) {
      throw new Error('Crew member not found.');
    } else if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else {
      throw new Error('Failed to update admin status. Please try again.');
    }
  }
};

export const updateCrewMember = async (projectId, memberId, updateData) => {
  if (!projectId || !memberId) {
    throw new Error('Project ID and Member ID are required');
  }

  try {
    console.log('Updating crew member:', { projectId, memberId, updateData });
    
    const response = await api.put(`/crew/projects/${projectId}/crew/${memberId}`, updateData);
    
    console.log('Crew member updated successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error updating crew member:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error(error.message || 'Failed to update crew member');
  }
};

export const addCrewMember = async (projectId, memberData) => {
  if (!projectId) {
    throw new Error('Project ID is required');
  }

  // Clean up the data
  const cleanedData = {
    userId: memberData.userId || memberData.user?._id,
    role: memberData.role?.trim() || undefined,
    department: memberData.department?.trim() || undefined
  };

  try {
    const response = await api.post(`/crew/projects/${projectId}/crew`, cleanedData);
    return response.data;
  } catch (error) {
    console.error('Error adding crew member:', error);
    throw error.response?.data || { message: 'Failed to add crew member' };
  }
};

export const sendInvitation = async (projectId, invitationData) => {
  if (!projectId) {
    throw new Error('Project ID is required');
  }

  if (!invitationData) {
    throw new Error('Invitation data is required');
  }

  // Validate required fields
  if (!invitationData.name?.trim()) {
    throw new Error('Name is required');
  }

  if (!invitationData.email?.trim()) {
    throw new Error('Email is required');
  }

  if (!/\S+@\S+\.\S+/.test(invitationData.email)) {
    throw new Error('Invalid email format');
  }

  const cleanedData = {
    name: invitationData.name.trim(),
    email: invitationData.email.trim(),
    role: invitationData.role?.trim() || undefined,
    department: invitationData.department?.trim() || undefined,
    userId: invitationData.userId
  };

  try {
    console.log('Sending invitation:', { projectId, cleanedData });
    const response = await api.post(
      `/crew/projects/${projectId}/invitations`,
      cleanedData
    );
    console.log('Invitation sent:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error sending invitation:', {
      error: error.response?.data || error.message,
      status: error.response?.status,
      projectId,
      data: cleanedData
    });

    // Handle specific error cases
    if (error.response?.status === 401) {
      throw new Error('Authentication required. Please log in again.');
    } else if (error.response?.status === 403) {
      throw new Error('You do not have permission to send invitations.');
    } else if (error.response?.status === 404) {
      throw new Error('Project not found.');
    } else if (error.response?.status === 400) {
      const errorMessage = error.response?.data?.message || 'Invalid invitation data';
      throw new Error(errorMessage);
    } else if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else {
      throw new Error('Failed to send invitation. Please try again.');
    }
  }
};

export const removeCrewMember = async (projectId, memberId) => {
  if (!projectId) {
    throw new Error('Project ID is required');
  }

  if (!memberId) {
    throw new Error('Member ID is required');
  }

  try {
    console.log('Removing crew member:', { projectId, memberId });
    const response = await api.delete(`/crew/projects/${projectId}/members/${memberId}`);
    
    console.log('Crew member removed:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error removing crew member:', {
      error: error.response?.data || error.message,
      status: error.response?.status,
      projectId,
      memberId
    });
    
    // Handle specific error cases
    if (error.response?.status === 401) {
      throw new Error('Authentication required. Please log in again.');
    } else if (error.response?.status === 403) {
      throw new Error('You do not have permission to remove this crew member.');
    } else if (error.response?.status === 404) {
      throw new Error('Crew member not found.');
    } else if (error.response?.status === 400) {
      // Handle validation errors
      const errorMessage = error.response?.data?.errors?.[0] || error.response?.data?.message || 'Invalid request';
      throw new Error(errorMessage);
    } else if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else {
      throw new Error('Failed to remove crew member. Please try again.');
    }
  }
};

export const acceptInvitation = async (token) => {
  if (!token || typeof token !== 'string') {
    throw new Error('Invitation token must be a non-empty string');
  }

  try {
    const response = await api.post(`/crew/invitations/${token}/accept`);
    return response.data;
  } catch (error) {
    console.error('Error accepting invitation:', error);
    throw error.response?.data || { message: 'Failed to accept invitation' };
  }
};

export const removeInvitation = async (projectId, invitationId) => {
  if (!projectId) {
    throw new Error('Project ID is required');
  }

  if (!invitationId) {
    throw new Error('Invitation ID is required');
  }

  try {
    console.log('Removing invitation:', { projectId, invitationId });
    const response = await api.delete(`/crew/projects/${projectId}/invitations/${invitationId}`);
    
    console.log('Invitation removed:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error removing invitation:', {
      error: error.response?.data || error.message,
      status: error.response?.status,
      projectId,
      invitationId
    });
    
    // Handle specific error cases
    if (error.response?.status === 401) {
      throw new Error('Authentication required. Please log in again.');
    } else if (error.response?.status === 403) {
      throw new Error('You do not have permission to remove this invitation.');
    } else if (error.response?.status === 404) {
      throw new Error('Invitation not found.');
    } else if (error.response?.status === 400) {
      // Handle validation errors
      const errorMessage = error.response?.data?.errors?.[0] || error.response?.data?.message || 'Invalid request';
      throw new Error(errorMessage);
    } else if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else {
      throw new Error('Failed to remove invitation. Please try again.');
    }
  }
};

// Valid roles and departments (matching server-side enums)
const VALID_ROLES = [
  'director', 'first-assistant-director', 'second-assistant-director', 'second-second-ad',
  'script-supervisor', 'additional-assistant-director', 'director-pa',
  'executive-producer', 'producer', 'line-producer', 'unit-production-manager',
  'production-supervisor', 'production-coordinator', 'production-secretary',
  'production-accountant', 'production-assistant', 'set-pa',
  'director-of-photography', 'camera-operator', 'first-ac', 'first-assistant-camera',
  'second-ac', 'second-assistant-camera', 'loader', 'camera-utility', 'dit', 'steadicam-operator', 'camera-pa',
  'gaffer', 'best-boy-electric', 'electric', 'genny-operator', 'board-operator',
  'rigging-electric', 'electric-utility', 'electric-pa',
  'key-grip', 'best-boy-grip', 'dolly-grip', 'grip', 'rigging-grip',
  'grip-utility', 'grip-pa',
  'production-sound-mixer', 'boom-operator', 'sound-utility', 'sound-pa',
  'a2', 'playback-operator', 'sound-assistant',
  'production-designer', 'art-director', 'assistant-art-director',
  'art-department-coordinator', 'set-designer', 'graphic-designer',
  'set-decorator', 'leadman', 'set-dresser', 'props-master',
  'props-assistant', 'art-pa',
  'construction-coordinator', 'head-carpenter', 'carpenter', 'scenic-artist',
  'painter', 'construction-grip', 'construction-pa',
  'costume-designer', 'assistant-costume-designer', 'costume-supervisor',
  'key-costumer', 'set-costumer', 'costume-shopper', 'seamstress-tailor',
  'costume-pa',
  'makeup-department-head', 'key-makeup-artist', 'special-effects-makeup',
  'makeup-artist', 'makeup-assistant', 'prosthetics-artist', 'makeup-pa',
  'hair-department-head', 'key-hair-stylist', 'hair-stylist', 'wig-master',
  'hair-assistant', 'hair-pa',
  'post-production-supervisor', 'editor', 'assistant-editor', 'colorist',
  'post-sound-mixer', 'sound-designer', 'composer', 'vfx-supervisor',
  'vfx-artist', 'post-pa',
  'office-production-assistant', 'office-coordinator', 'travel-coordinator',
  'payroll-accountant', 'office-pa',
  'location-manager', 'assistant-location-manager', 'location-scout', 'location-pa',
  'transportation-coordinator', 'transportation-captain', 'driver',
  'picture-car-coordinator',
  'unit-publicist', 'epk-producer', 'still-photographer', 'social-media-coordinator',
  'craft-service', 'craft-assistant', 'catering-manager', 'chef', 'food-assistant',
  'stunt-coordinator', 'stunt-performer', 'fight-choreographer', 'safety-supervisor',
  'supervisor', 'coordinator', 'assistant', 'specialist', 'other'
];

const VALID_DEPARTMENTS = [
  'direction',
  'production',
  'camera',
  'electric',
  'grip',
  'sound',
  'art',
  'construction',
  'costume',
  'makeup',
  'hair',
  'post-production',
  'production-office',
  'locations',
  'transportation',
  'publicity',
  'craft',
  'stunts',
  'other'
];

const DEPARTMENT_ROLES = {
  'direction': ['director', 'first-assistant-director', 'second-assistant-director', 'second-second-ad', 'script-supervisor', 'additional-assistant-director', 'director-pa'],
  'production': ['executive-producer', 'producer', 'line-producer', 'unit-production-manager', 'production-supervisor', 'production-coordinator', 'production-secretary', 'production-accountant', 'production-assistant', 'set-pa'],
  'camera': ['director-of-photography', 'camera-operator', 'first-ac', 'first-assistant-camera', 'second-ac', 'second-assistant-camera', 'loader', 'camera-utility', 'dit', 'steadicam-operator', 'camera-pa'],
  'electric': ['gaffer', 'best-boy-electric', 'electric', 'genny-operator', 'board-operator', 'rigging-electric', 'electric-utility', 'electric-pa'],
  'grip': ['key-grip', 'best-boy-grip', 'dolly-grip', 'grip', 'rigging-grip', 'grip-utility', 'grip-pa'],
  'sound': ['production-sound-mixer', 'boom-operator', 'sound-utility', 'sound-pa', 'a2', 'playback-operator', 'sound-assistant'],
  'art': ['production-designer', 'art-director', 'assistant-art-director', 'art-department-coordinator', 'set-designer', 'graphic-designer', 'set-decorator', 'leadman', 'set-dresser', 'props-master', 'props-assistant', 'art-pa'],
  'construction': ['construction-coordinator', 'head-carpenter', 'carpenter', 'scenic-artist', 'painter', 'construction-grip', 'construction-pa'],
  'costume': ['costume-designer', 'assistant-costume-designer', 'costume-supervisor', 'key-costumer', 'set-costumer', 'costume-shopper', 'seamstress-tailor', 'costume-pa'],
  'makeup': ['makeup-department-head', 'key-makeup-artist', 'special-effects-makeup', 'makeup-artist', 'makeup-assistant', 'prosthetics-artist', 'makeup-pa'],
  'hair': ['hair-department-head', 'key-hair-stylist', 'hair-stylist', 'wig-master', 'hair-assistant', 'hair-pa'],
  'post-production': ['post-production-supervisor', 'editor', 'assistant-editor', 'colorist', 'post-sound-mixer', 'sound-designer', 'composer', 'vfx-supervisor', 'vfx-artist', 'post-pa'],
  'production-office': ['office-production-assistant', 'office-coordinator', 'travel-coordinator', 'payroll-accountant', 'office-pa'],
  'locations': ['location-manager', 'assistant-location-manager', 'location-scout', 'location-pa'],
  'transportation': ['transportation-coordinator', 'transportation-captain', 'driver', 'picture-car-coordinator'],
  'publicity': ['unit-publicist', 'epk-producer', 'still-photographer', 'social-media-coordinator'],
  'craft': ['craft-service', 'craft-assistant', 'catering-manager', 'chef', 'food-assistant'],
  'stunts': ['stunt-coordinator', 'stunt-performer', 'fight-choreographer', 'safety-supervisor'],
  'other': ['supervisor', 'coordinator', 'assistant', 'specialist', 'other']
};

export const createCrewMember = async (projectId, { userId, role, department }) => {
  if (!userId) {
    throw new Error('User ID is required');
  }

  // Basic pairing checks; defer canonical validation to server
  if (role && !department) {
    throw new Error('Department is required when role is specified');
  }
  if (department && !role) {
    throw new Error('Role is required when department is specified');
  }

  try {
    const response = await api.post(`/crew/projects/${projectId}/crew`, {
      userId,
      role: role || null,
      department: department || null
    });
    
    if (response.data?.crewMember) {
      return {
        crewMember: {
          ...response.data.crewMember,
          role: role || null,
          department: department || null
        }
      };
    }
    return response.data;
  } catch (error) {
    console.error('Error creating crew member:', error);
    
    // Handle validation errors from server
    if (error.response?.data?.errors) {
      const err = new Error(error.response.data.message || 'Validation error');
      err.errors = error.response.data.errors;
      throw err;
    }
    
    // Handle other errors
    const err = new Error(error.response?.data?.message || 'Failed to create crew member');
    err.errors = [error.response?.data?.message || 'Failed to create crew member'];
    throw err;
  }
};
